import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Toast, SpinLoading } from 'antd-mobile'
import { CheckRecord } from '@/types'
import { getCheckRecord, saveCheckRecord } from '@/services/storage'
import { generateReportData } from '@/utils/report'
import { generatePDF } from '@/utils/pdf'
import { ReportTemplate, SignaturePadComponent } from '@/components'

export const ReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [record, setRecord] = useState<CheckRecord | null>(null)
  const [showSignature, setShowSignature] = useState<'inspector' | 'unit' | null>(null)

  useEffect(() => {
    if (id) {
      loadRecord(id)
    }
  }, [id])

  const loadRecord = async (recordId: string) => {
    setLoading(true)
    try {
      const data = await getCheckRecord(recordId)
      if (data) {
        setRecord(data)
      } else {
        Toast.show({ content: '报告不存在', icon: 'fail' })
        navigate('/')
      }
    } catch (error) {
      console.error('加载报告失败:', error)
      Toast.show({ content: '加载报告失败', icon: 'fail' })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSignature = async (dataUrl: string) => {
    if (!record || !showSignature) return

    const updatedRecord = { ...record }
    if (!updatedRecord.report) {
      updatedRecord.report = {
        id: record.id + '_report',
        checkRecordId: record.id,
        reportNumber: '',
        content: '',
        generatedAt: new Date(),
      }
    }

    if (showSignature === 'inspector') {
      updatedRecord.report.inspectorSignature = dataUrl
    } else {
      updatedRecord.report.unitRepresentativeSignature = dataUrl
    }

    try {
      await saveCheckRecord(updatedRecord)
      setRecord(updatedRecord)
      setShowSignature(null)
      Toast.show({ content: '签名保存成功', icon: 'success' })
    } catch (error) {
      console.error('保存签名失败:', error)
      Toast.show({ content: '保存签名失败', icon: 'fail' })
    }
  }

  const handleExportPDF = () => {
    if (!record) return

    try {
      const reportData = generateReportData(record)
      generatePDF(reportData)
      Toast.show({ content: 'PDF导出成功', icon: 'success' })
    } catch (error) {
      console.error('导出PDF失败:', error)
      Toast.show({ content: '导出PDF失败', icon: 'fail' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <SpinLoading color="#c62828" />
        <p className="mt-4 text-gray-600">正在加载报告...</p>
      </div>
    )
  }

  if (!record) return null

  const reportData = generateReportData(record)

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* 顶部标题栏 */}
      <div className="bg-red-700 text-white px-4 py-4 sticky top-0 z-10 print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate('/')} className="mr-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <h1 className="text-lg font-bold">检查报告</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="text-sm">
              打印
            </button>
            <button onClick={handleExportPDF} className="text-sm bg-white text-red-700 px-3 py-1 rounded">
              导出PDF
            </button>
          </div>
        </div>
      </div>

      {/* 报告内容 */}
      <div className="print:shadow-none">
        <ReportTemplate data={reportData} />
      </div>

      {/* 签名按钮 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 print:hidden">
        <div className="flex gap-3">
          <button
            onClick={() => setShowSignature('inspector')}
            className={`flex-1 py-2.5 rounded-lg font-medium ${
              record.report?.inspectorSignature
                ? 'bg-green-50 text-green-600 border border-green-200'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {record.report?.inspectorSignature ? '✓ 已签名' : '检查人员签名'}
          </button>
          <button
            onClick={() => setShowSignature('unit')}
            className={`flex-1 py-2.5 rounded-lg font-medium ${
              record.report?.unitRepresentativeSignature
                ? 'bg-green-50 text-green-600 border border-green-200'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {record.report?.unitRepresentativeSignature ? '✓ 已签名' : '单位代表签名'}
          </button>
        </div>
      </div>

      {/* 签名弹窗 */}
      {showSignature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center print:hidden">
          <div className="bg-white w-full max-w-lg rounded-t-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">
                {showSignature === 'inspector' ? '检查人员签名' : '单位代表签名'}
              </h3>
              <button onClick={() => setShowSignature(null)} className="text-gray-400">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <SignaturePadComponent
              onSave={handleSaveSignature}
              onCancel={() => setShowSignature(null)}
              height={200}
            />
          </div>
        </div>
      )}
    </div>
  )
}
